import { formatTimeAgo } from '@tendedloop/shared/i18n';
/**
 * Parking anchor, localStorage-only "remember where I parked" helper.
 *
 * The design (see docs/product/visitor-pwa/parking-anchor-2026-04-18.md) is:
 * - Anchor on a QR scan where `anchorable === true` (parking amenity types).
 * - Recall on any later scan of the same tenant + building.
 * - Forget via TTL (per-tenant) + local-day boundary + explicit clear.
 *
 * Nothing in this module hits the network. The photo key (if any) points to a
 * tenant-scoped GCS object uploaded via the existing presigned-URL flow.
 */

export interface ParkedSpot {
  tenantId: string;
  tenantName: string;
  buildingId: string;
  buildingName: string;

  amenityId: string;
  amenityName: string;
  locationName: string;
  level: number | null;

  /** Epoch ms when the anchor was created (or last replaced). */
  ts: number;
  /** YYYY-MM-DD in the visitor's local tz at anchor time, used for day-boundary clears. */
  localDate: string;
  /** TTL in hours, from the tenant's `parkingAnchorTTLHours` (or 12 default). */
  ttlHours: number;

  /** Optional memory aids. photoThumb is a compressed jpeg as dataURL, kept
   *  local-only, no GCS round-trip, no identity attached. */
  photoThumb?: string;
  note?: string;

  /** Cached RAG prose per-source-amenity so recall is instant on repeat. */
  directions?: Record<string, string>;

  /** Last time the visitor tapped "Still there?", extends effective freshness. */
  lastConfirmedTs?: number;
}

const STORAGE_KEY_PREFIX = 'tl_parking:';

function keyFor(tenantId: string, buildingId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}:${buildingId}`;
}

function todayLocalDate(): string {
  // ISO-8601 date in the visitor's local tz: YYYY-MM-DD
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function safeRead<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Write an anchor. If one already exists for the same (tenant, building), overwrites. */
export function saveAnchor(input: Omit<ParkedSpot, 'ts' | 'localDate'> & { ts?: number }): ParkedSpot {
  const now = input.ts ?? Date.now();
  const spot: ParkedSpot = {
    ...input,
    ts: now,
    localDate: todayLocalDate(),
  };
  try {
    localStorage.setItem(keyFor(spot.tenantId, spot.buildingId), JSON.stringify(spot));
  } catch {
    /* quota / private mode, anchor simply won't persist. Fail silent. */
  }
  return spot;
}

/** Patch an existing anchor (merge fields). No-op if the anchor is gone. */
export function updateAnchor(
  tenantId: string,
  buildingId: string,
  patch: Partial<Omit<ParkedSpot, 'tenantId' | 'buildingId'>>,
): ParkedSpot | null {
  const existing = readAnchor(tenantId, buildingId);
  if (!existing) return null;
  const updated: ParkedSpot = { ...existing, ...patch };
  try {
    localStorage.setItem(keyFor(tenantId, buildingId), JSON.stringify(updated));
  } catch {
    /* see saveAnchor */
  }
  return updated;
}

/**
 * Read + sweep in one step. Returns the anchor only if it's still within TTL
 * and same local day. Expired anchors are cleared as a side-effect so the
 * storage tombstone doesn't linger.
 */
export function readAnchor(tenantId: string, buildingId: string): ParkedSpot | null {
  try {
    const raw = localStorage.getItem(keyFor(tenantId, buildingId));
    const spot = safeRead<ParkedSpot>(raw);
    if (!spot) return null;

    const ageMs = Date.now() - spot.ts;
    const ttlMs = spot.ttlHours * 60 * 60 * 1000;
    const dayChanged = spot.localDate !== todayLocalDate();
    const isHotel = spot.ttlHours >= 24; // hotels use time-based, not day-based
    const expired = ageMs > ttlMs || (dayChanged && !isHotel);

    if (expired) {
      localStorage.removeItem(keyFor(tenantId, buildingId));
      return null;
    }
    return spot;
  } catch {
    return null;
  }
}

export function clearAnchor(tenantId: string, buildingId: string): void {
  try {
    localStorage.removeItem(keyFor(tenantId, buildingId));
  } catch {
    /* ignore */
  }
}

/**
 * True when the anchor is past half its TTL, we nudge the visitor with a
 * "Still parked here?" check on recall, but don't clear yet.
 */
export function isStale(spot: ParkedSpot): boolean {
  const halfTtlMs = (spot.ttlHours * 60 * 60 * 1000) / 2;
  const refTs = spot.lastConfirmedTs ?? spot.ts;
  return Date.now() - refTs > halfTtlMs;
}

/** Human-friendly relative time in the visitor's language ("5 分前", "לפני 5 דקות"). */
export function relativeTime(ts: number, locale?: string): string {
  return formatTimeAgo(ts, locale || (typeof navigator !== 'undefined' ? navigator.language : 'en'));
}
